import React, { useState, useEffect, useContext } from 'react';
import './Orders.css';
import { UrlContext } from '../../Context/UrlContext';

function Orders() {

     const { apiBaseUrl } = useContext(UrlContext);

  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [statusFilter, setStatusFilter] = useState('all');
  const [statusUpdateMessage, setStatusUpdateMessage] = useState({ type: '', text: '' });

  useEffect(() => {
    fetchOrders();
  }, []);

  const fetchOrders = async () => {
    try {
      console.log('Fetching orders...');
      const response = await fetch(`${apiBaseUrl}/orders`);
      console.log('Response status:', response.status);
      
      if (!response.ok) {
        const errorData = await response.json();
        console.error('Server error:', errorData);
        throw new Error(errorData.details || errorData.error || 'Failed to fetch orders');
      }

      const data = await response.json();
      console.log('Received orders data:', data);

      if (!Array.isArray(data)) {
        console.error('Invalid data format:', data);
        throw new Error('Invalid data format received');
      }

      setOrders(data);
      setError(null);
    } catch (err) {
      console.error('Error in fetchOrders:', err);
      setError(err.message || 'Failed to load orders. Please try again later.');
    } finally {
      setLoading(false);
    }
  };

  const handleStatusChange = async (orderId, newStatus) => {
    try {
      console.log(`Updating order ${orderId} status to ${newStatus}`);
      const response = await fetch(`${apiBaseUrl}/orders/${orderId}/status`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ status: newStatus })
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to update order status');
      }

      setStatusUpdateMessage({ type: 'success', text: 'Order status updated successfully' });
      await fetchOrders(); // Refresh orders list

      setTimeout(() => {
        setStatusUpdateMessage({ type: '', text: '' });
      }, 3000);
    } catch (err) {
      console.error('Error updating status:', err);
      setStatusUpdateMessage({ type: 'error', text: err.message });
    }
  };

  const filteredOrders = orders.filter(order => 
    statusFilter === 'all' ? true : order.status === statusFilter
  );

  if (loading) return <div className="loading">Loading orders...</div>;
  if (error) return (
    <div className="error-container">
      <div className="error">{error}</div>
      <button onClick={fetchOrders} className="retry-button">
        Retry
      </button>
    </div>
  );

  return (
    <div className='orders_container'>
      {statusUpdateMessage.text && (
        <div className={`message ${statusUpdateMessage.type}`}>
          {statusUpdateMessage.text}
        </div>
      )}

      <div className="orders_filter">
        <h2>Orders</h2>
        <div className="filter_controls">
          <label htmlFor="statusFilter">Filter by Status:</label>
          <select
            id="statusFilter"
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
          >
            <option value="all">All Orders</option>
            <option value="pending">Pending</option>
            <option value="processing">Processing</option>
            <option value="shipped">Shipped</option>
            <option value="delivered">Delivered</option>
            <option value="cancelled">Cancelled</option>
          </select>
        </div>
      </div>
      
      <div className="orders_list">
        {filteredOrders.length === 0 ? (
          <div className="no-orders">
            {statusFilter === 'all' 
              ? 'No orders found' 
              : `No ${statusFilter} orders found`}
          </div>
        ) : (
          filteredOrders.map((order) => (
            <div key={order.order_id} className="order_card">
              <div className="order_header">
                <div className="order_info">
                  <h3>Order #{order.order_id}</h3>
                  <p>Date: {new Date(order.created_at).toLocaleDateString()}</p>
                </div>
                <div className="status_control">
                  <select
                    value={order.status}
                    onChange={(e) => handleStatusChange(order.order_id, e.target.value)}
                    className={`status_${order.status}`}
                  >
                    <option value="pending">Pending</option>
                    <option value="processing">Processing</option>
                    <option value="shipped">Shipped</option>
                    <option value="delivered">Delivered</option>
                    <option value="cancelled">Cancelled</option>
                  </select>
                </div>
              </div>

              <div className="customer_info">
                <h4>Customer Details</h4>
                <div className="customer_grid">
                  <div className="customer_detail">
                    <strong>Name:</strong> {order.first_name} {order.last_name}
                  </div>
                  <div className="customer_detail">
                    <strong>Email:</strong> {order.email}
                  </div>
                  <div className="customer_detail">
                    <strong>Phone:</strong> {order.phone_number}
                  </div>
                  <div className="customer_detail">
                    <strong>Address:</strong> {order.address}
                  </div>
                  <div className="customer_detail">
                    <strong>Location:</strong> {order.country}, {order.postal_code}
                  </div>
                </div>
              </div>

              <div className="order_items">
                <h4>Order Items</h4>
                <div className="order_items_table">
                  <table>
                    <thead>
                      <tr>
                        <th>Image</th>
                        <th>Product Name</th>
                        <th>Size</th>
                        <th>Unit Price</th>
                        <th>Quantity</th>
                        <th>Total</th>
                        <th className="hide-mobile">Cart Order ID</th>
                        <th className="hide-mobile">Added Date</th>
                      </tr>
                    </thead>
                    <tbody>
                      {order.cart_items.map((item) => (
                        <tr key={item.cart_order_id}>
                          <td className="product_image_cell">
                            <img 
                              src={`data:image/jpeg;base64,${item.product_image}`} 
                              alt={item.product_name} 
                            />
                          </td>
                          <td className="product_details_cell">
                            <h5>{item.product_name}</h5>
                            <div className="mobile-only">
                              <p>Size: {item.size}</p>
                              <p>Cart ID: #{item.cart_order_id}</p>
                              <p>Added: {new Date(item.created_at).toLocaleDateString()}</p>
                            </div>
                          </td>
                          <td className="hide-mobile">{item.size}</td>
                          <td>Rs. {item.unit_price}</td>
                          <td>{item.quantity}</td>
                          <td>Rs. {item.item_total}</td>
                          <td className="hide-mobile">#{item.cart_order_id}</td>
                          <td className="hide-mobile">{new Date(item.created_at).toLocaleDateString()}</td>
                        </tr>
                      ))}
                    </tbody>
                    <tfoot>
                      <tr>
                        <td colSpan="3" className="text-right"><strong>Subtotal:</strong></td>
                        <td colSpan="5">Rs. {order.sub_total}</td>
                      </tr>
                      <tr>
                        <td colSpan="3" className="text-right"><strong>Shipping Fee:</strong></td>
                        <td colSpan="5">Rs. {order.shipping_fee}</td>
                      </tr>
                      <tr className="total-row">
                        <td colSpan="3" className="text-right"><strong>Total:</strong></td>
                        <td colSpan="5">Rs. {order.total}</td>
                      </tr>
                    </tfoot>
                  </table>
                </div>
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
}

export default Orders;
